import { List, ListItem, ListItemText } from "@material-ui/core";
import { createStyles, makeStyles } from "@material-ui/styles";
import {
    CircleIcon,
    Divider,
    FeedbackModal,
    FeedbackSearchIcons,
    FeedbackTypeEnum,
    FilterIcon,
    ISearchFacetConfig,
    Link,
    MessageComposerIconButton,
    MobilePage,
    MobilePageActions,
    MobilePageSection,
    PaginatorMobile,
    RelodeTheme,
    SearchFiltersMobile,
    SortMobile,
    SortOption,
    StatusLabel,
    Text,
    useNotifications,
    useRouter,
} from "@relode/core";
import SortIcon from "@relode/core/assets/icons/sortIcon";
import qs from "qs";
import * as React from "react";

import { candidates } from "../../../shared/backend/controllers";
import { useSearch } from "../_infra/search/candidateSearchProvider";

const useStyles = makeStyles(
    (theme: RelodeTheme) =>
        createStyles({
            keywordInput: {
                margin: theme.spacing(1, 0, 2, 0),
            },
            searchButton: {
                padding: 0,
            },
            searchButtonIcon: {
                fontSize: "1rem",
            },
            statusSection: {
                display: "flex",
                margin: theme.spacing(1, 0),
            },
            statusLabel: {
                marginRight: 0,
                marginTop: theme.spacing(0.25),
            },
            circle: {
                fontSize: "0.5em",
                color: theme.palette.action.active,
                marginTop: theme.spacing(1),
                marginRight: theme.spacing(1),
                marginLeft: theme.spacing(1),
            },
            candidateActions: {
                display: "flex",
                justifyContent: "space-evenly",
                width: "100%",
            },
            candidateListItemData: {
                backgroundColor: theme.palette.common.white,
            },
            candidateListItemActions: {
                backgroundColor: theme.palette.common.white,
                padding: 0,
            },
            listItemContent: {
                paddingLeft: theme.spacing(2),
                paddingRight: theme.spacing(2),
            },
            sortAndFilterContainer: {
                position: "absolute",
                right: 0,
                marginRight: theme.spacing(2),
            },
            sortLink: { marginRight: theme.spacing(3) },
            sortIcon: {
                paddingTop: "2px",
            },
            noResultsText: {
                margin: theme.spacing(5),
            },
        }),
    { name: "CandidateSearchMobile" }
);

const NOT_SHARED_JOB_MESSAGE =
    "You are not shared to this job. You must be shared to this job to give feedback.";
const JOB_NOT_CORRECT_STATUS_MESSAGE =
    "Feedback can only be given when a candidate is in Submitted or Interviewing status.";

export const CandidateSearchMobile = () => {
    const classes = useStyles({});
    const [searchState, searchActions] = useSearch();
    const { actions: notifications } = useNotifications();
    const { history, location } = useRouter();

    const [backUrl, setBackUrl] = React.useState<string>(undefined);

    const [isFiltersSectionOpen, setIsFiltersSectionOpen] =
        React.useState<boolean>(false);
    const [isSortOpen, setIsSortOpen] = React.useState<boolean>(false);

    const [isFeedbackModalOpen, setIsFeedbackModalOpen] =
        React.useState<boolean>(false);
    const [feedbackModalProps, setFeedbackModalProps] = React.useState<{
        jobName: string;
        candidateFullName: string;
        leadId: number;
        jobId: number;
        comment: string;
        selectedFeedbackType: FeedbackTypeEnum;
        hasGivenFeedback: boolean;
    }>(undefined);

    const { searchResults, searchCriteria } = searchState;

    const handleSearch = (searchText: string) => {
        searchActions.searchText(searchText);
        setIsFiltersSectionOpen(false);
        window.scrollTo(0, 0);
    };

    React.useEffect(() => {
        if (!!location.search) {
            setBackUrl(`/candidates${location.search}`);
        } else {
            setBackUrl("/candidates");
        }
    }, [location.search]);

    const handleMultipleSelectionFilter =
        (fId: string, fName: string, itemName?: string) =>
        (catItem?: { label: string; value: string }) => {
            searchActions.filterMultiple(fId, fName, itemName, catItem);
        };

    const handleFilter =
        (fId: string, fName: string) =>
        (catItem?: { label: string; value: string }) => {
            searchActions.filterSingle(fId, fName, catItem);
        };

    const openFeedbackModal = (
        selectedFeedbackType: FeedbackTypeEnum,
        jobId: number,
        jobName: string,
        leadId: number,
        candidateFullName: string,
        comment: string,
        canGiveFeedback: boolean,
        hasGivenFeedback: boolean,
        isJobUser: boolean
    ) => {
        if (canGiveFeedback) {
            setFeedbackModalProps({
                jobId,
                leadId,
                candidateFullName,
                jobName,
                comment,
                selectedFeedbackType,
                hasGivenFeedback: hasGivenFeedback,
            });

            setIsFeedbackModalOpen(true);
        } else if (!isJobUser) {
            notifications.showModal(
                "info",
                `Unable to change feedback for ${candidateFullName}`,
                NOT_SHARED_JOB_MESSAGE
            );
        } else {
            notifications.showModal(
                "info",
                `Unable to change feedback for ${candidateFullName}`,
                JOB_NOT_CORRECT_STATUS_MESSAGE
            );
        }
    };

    const addOrUpdateFeedback = async (
        comment: string,
        vote: boolean,
        hasGivenFeedback: boolean,
        jobId: number,
        leadId: number
    ) => {
        if (hasGivenFeedback) {
            await searchActions.updateFeedback(jobId, leadId, vote, comment);
        } else {
            await searchActions.addFeedback(jobId, leadId, vote, comment);
        }
        setIsFeedbackModalOpen(false);
    };

    const facetConfig: ISearchFacetConfig = {
        handleMultipleSelection: handleMultipleSelectionFilter,
        handleSingleSelection: handleFilter,
        facets: [
            {
                id: "candidateStatus",
                title: "candidate status",
                idPropName: "jobCandidateStatusIds",
                namePropName: "jobCandidateStatusNames",
                isMultiple: true,
            },
            {
                id: "location",
                title: "job location",
                idPropName: "locationId",
                namePropName: "locationName",
                isMultiple: false,
            },
            {
                id: "jobTitle",
                title: "job title",
                idPropName: "jobId",
                namePropName: "jobTitleName",
                isMultiple: false,
            },
            {
                id: "jobStatus",
                title: "job status",
                idPropName: "jobStatusIds",
                namePropName: "jobStatusNames",
                isMultiple: true,
            },
            {
                id: "jobType",
                title: "job type",
                idPropName: "jobTypeIds",
                namePropName: "jobTypeNames",
                isMultiple: true,
            },
            {
                id: "team",
                title: "organization",
                idPropName: "teamId",
                namePropName: "TeamName",
                isMultiple: false,
            },
            {
                id: "jobScope",
                title: "job scope",
                idPropName: "jobScope",
                namePropName: "jobScopeName",
                isMultiple: false,
            },
            // These are "fake" facets; they are used only the display the query params values to refined by section;
            // if those filters are removed than these facets will not be displayed since they are not returned by API endpoint
            {
                id: "job",
                title: "job",
                idPropName: "jobId",
                namePropName: "jobName",
                isMultiple: false,
            },
        ],
    };

    const getSortOptions = (): SortOption[] => [
        {
            field: "candidateFullName",
            title: "Candidate: A to Z",
            direction: "asc",
        },
        {
            field: "jobName",
            title: "Job: A to Z",
            direction: "asc",
        },
        {
            field: "candidateStatusName",
            title: "Status: From Submitted",
            direction: "asc",
        },
        {
            field: "candidateStatusName",
            title: "Status: From Hired",
            direction: "desc",
        },
        {
            field: "daysInStatus",
            title: "Days in Status: High to Low",
            direction: "desc",
        },
        {
            field: "daysInStatus",
            title: "Days in Status: Low to High",
            direction: "asc",
        },
        {
            field: "positiveVoteCount",
            title: "Feedback: High to Low",
            direction: "desc",
        },
        {
            field: "positiveVoteCount",
            title: "Feedback: Low to High",
            direction: "asc",
        },
    ];

    const handleRequestSort = (field: string, direction: SortDir) => {
        searchActions.sortMobile(field, direction);
    };

    const goToDetails = (candidate: candidates.CandidateSearchLeadResponse) => {
        const { leadIdAndJobIds } = searchResults;
        const queryString = qs.stringify(searchCriteria, {
            skipNulls: true,
            sort: (a, b) => a.localeCompare(b),
        });

        history.push({
            pathname: `/candidates/${candidate.jobId}/${candidate.leadId}`,
            state: {
                ...location.state,
                leadIdAndJobIds,
                queryString,
                backUrl,
            },
        });
    };

    const changePage = (direction: "prev" | "next") => {
        searchActions.navigate(direction, searchResults?.totalResults);
        window.scrollTo(0, 0);
    };

    const renderPagination = (): React.ReactNode => {
        const { pageSize, pageIndex } = searchCriteria;

        const start = (pageIndex - 1) * pageSize + 1;
        const remaining =
            searchResults?.totalResults < pageSize
                ? searchResults?.totalResults
                : searchResults?.totalResults - start;
        const maxOnPage =
            remaining < pageSize
                ? searchResults?.totalResults
                : pageIndex * pageSize;

        if (!!searchResults && searchResults?.totalResults > 0) {
            return (
                <PaginatorMobile
                    baseId="searchCandidates"
                    total={searchResults?.totalResults}
                    range={{
                        start,
                        end: maxOnPage,
                    }}
                    showAsRange
                    navigate={changePage}
                />
            );
        } else {
            return null;
        }
    };

    return (
        <>
            <MobilePage
                pageTitle="candidates"
                defaultSection="search"
                showTabs={false}
                busyType="bar"
                isBusy={searchState.searching}
                actions={
                    <MobilePageActions title="candidates">
                        <div className={classes.sortAndFilterContainer}>
                            <Link
                                className={classes.sortLink}
                                onClick={() => setIsSortOpen(true)}
                            >
                                <Text variant="body1" color="primary">
                                    <SortIcon className={classes.sortIcon} />{" "}
                                    sort
                                </Text>
                            </Link>
                            <Link onClick={() => setIsFiltersSectionOpen(true)}>
                                <Text variant="body1" color="primary">
                                    <FilterIcon fontSize="small" /> filters
                                </Text>
                            </Link>
                        </div>
                    </MobilePageActions>
                }
            >
                <MobilePageSection id="search">
                    <div>
                        {(searchResults?.candidates?.length ?? 0) > 0 ? (
                            <List id="candidateListing">
                                {searchResults.candidates.map(
                                    (candidate, i) => (
                                        <CandidateListItem
                                            key={i}
                                            candidate={candidate}
                                            onClick={goToDetails}
                                            handleFeedbackClick={
                                                openFeedbackModal
                                            }
                                        />
                                    )
                                )}
                            </List>
                        ) : !searchState.searching ? (
                            <div className={classes.noResultsText}>
                                <Text variant="h4" color="primary">
                                    Sorry, no candidates found.
                                </Text>
                            </div>
                        ) : null}
                    </div>
                    {renderPagination()}
                </MobilePageSection>
            </MobilePage>
            <SortMobile
                isOpen={isSortOpen}
                handleIsOpen={(val: boolean) => setIsSortOpen(val)}
                handleRequestSort={handleRequestSort}
                searchCriteria={searchState.searchCriteria}
                sortOptions={getSortOptions()}
            />
            <SearchFiltersMobile
                isOpen={isFiltersSectionOpen}
                onOpenChanged={(val: boolean) => setIsFiltersSectionOpen(val)}
                searchCriteria={searchState.searchCriteria}
                onSearch={handleSearch}
                facets={searchResults?.facets}
                facetConfig={facetConfig}
                hasResults={searchResults?.totalResults > 0}
            />

            {isFeedbackModalOpen ? (
                <FeedbackModal
                    onCancel={() => setIsFeedbackModalOpen(false)}
                    onConfirm={addOrUpdateFeedback}
                    isLoading={searchState.searching}
                    {...feedbackModalProps}
                />
            ) : null}
        </>
    );
};

const CandidateListItem = (props: {
    candidate: candidates.CandidateSearchLeadResponse;
    onClick: (candidate: candidates.CandidateSearchLeadResponse) => void;
    handleFeedbackClick: (
        selectedFeedbackType: FeedbackTypeEnum,
        jobId: number,
        jobName: string,
        leadId: number,
        candidateFullName: string,
        comment: string,
        canGiveFeedback: boolean,
        hasGivenFeedback: boolean,
        isJobUser: boolean
    ) => void;
}) => {
    const { candidate, onClick, handleFeedbackClick } = props;
    const classes = useStyles({});

    const handleClick = () => {
        onClick(candidate);
    };

    const getSelectedFeedbackType = (
        vote: boolean,
        hasGivenFeedback: boolean
    ) => {
        if (hasGivenFeedback) {
            return vote ? FeedbackTypeEnum.Positive : FeedbackTypeEnum.Negative;
        }
        return null;
    };

    return (
        <>
            <ListItem
                button
                disableGutters
                alignItems="flex-start"
                onClick={handleClick}
                className={classes.candidateListItemData}
            >
                <ListItemText
                    primary={candidate.candidateFullName}
                    primaryTypographyProps={{
                        variant: "h5",
                        color: "primary",
                    }}
                    secondaryTypographyProps={{ component: "div" }}
                    secondary={
                        <>
                            <div className={classes.statusSection}>
                                <StatusLabel
                                    title={candidate.candidateStatusName}
                                    jobCandidateStatus={
                                        candidate.candidateStatusId
                                    }
                                    className={classes.statusLabel}
                                />
                                <CircleIcon className={classes.circle} />
                                <Text component="span">{`${candidate.daysInStatus} days in status`}</Text>
                            </div>
                            <Text component="span">{`${candidate.jobName} `}</Text>
                            <CircleIcon className={classes.circle} />
                            <Text component="span">{` ${candidate.locationName}`}</Text>
                        </>
                    }
                    className={classes.listItemContent}
                />
            </ListItem>
            <ListItem
                component="div"
                disableGutters
                alignItems="flex-start"
                className={classes.candidateListItemActions}
            >
                <div className={classes.candidateActions}>
                    <MessageComposerIconButton
                        leadId={candidate.leadId}
                        leadName={candidate.candidateFullName}
                        jobId={candidate.jobId}
                        jobName={candidate.jobName}
                    />
                    <div>
                        <FeedbackSearchIcons
                            onClick={handleFeedbackClick}
                            jobId={candidate.jobId}
                            jobName={candidate.jobName}
                            leadId={candidate.leadId}
                            fullName={candidate.candidateFullName}
                            comment={candidate.comment}
                            selectedFeedbackType={getSelectedFeedbackType(
                                candidate.vote,
                                candidate.hasGivenFeedback
                            )}
                            canGiveFeedback={candidate.canAddEditFeedback}
                            hasGivenFeedback={candidate.hasGivenFeedback}
                            showFeedbackCounts={candidate.canViewFeedback}
                            upCount={candidate.positiveVoteCount}
                            downCount={candidate.negativeVoteCount}
                            isJobUser={candidate.isJobUser}
                        />
                    </div>
                </div>
            </ListItem>
            <Divider></Divider>
        </>
    );
};
